import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { getCubeAPI } from '../../../../../Cube'
import { QueryRenderer } from '@cubejs-client/react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { Skeleton } from 'antd'
import { ReportFilterStore } from '../ReportFilterStore'
import { parseAndDownloadCSV, GrPrice } from '../../../../../utilities/helpers'

interface Props {
  companyId: number
  employeeId: string
  employeeName: string
  reportFilterStore?: ReportFilterStore
  employeeToReport?: string
  onClose: () => void
}

interface IOrder {
  field: string
  direction: 'asc' | 'desc'
}

interface State {
  order: IOrder
  selectedEmployee?: {
    id: string
    name: string
  }
}

@inject('reportFilterStore')
@observer
export class EmployeeCategoryReportModal extends React.Component<Props, State> {
  // Static property that stores loaded report data
  data: any

  constructor(props: Props) {
    super(props)
    this.state = {
      order: {
        field: 'PaymentLines.productNet',
        direction: 'desc',
      },
    }
  }

  toggleOrder(fieldName: string) {
    if (this.state.order.field === fieldName) {
      const currentDirection = this.state.order.direction
      this.setState({
        order: {
          field: fieldName,
          direction: currentDirection === 'asc' ? 'desc' : 'asc',
        },
      })
    } else {
      this.setState({
        order: {
          field: fieldName,
          direction: 'desc',
        },
      })
    }
  }

  tableRenderer = ({ resultSet }: any) => {
    const rawData: any[] = resultSet.rawData()
    if (rawData.length > 0) {
      return (
        <table className='table table-sm'>
          <thead>
            <tr>
              <th>
                <a onClick={() => this.toggleOrder('Categories.fullname')}>Category</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.productNet')}>Net sales</a>
              </th>
              <th className='text-right'>
                <a onClick={() => this.toggleOrder('PaymentLines.quantity')}>Quantity</a>
              </th>
            </tr>
          </thead>
          <tbody>
            {rawData.map((data, index) => {
              return (
                <tr key={index}>
                  <td>{data['Categories.fullname']}</td>
                  <td className='text-right'>
                    {GrPrice.convertDollarToDollar(data['PaymentLines.productNet'])}
                  </td>
                  <td className='text-right'>{data['PaymentLines.quantity']}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )
    } else {
      return <div>No data</div>
    }
  }

  renderChart(Component: any) {
    return ({ resultSet, error }: any) => {
      if (resultSet) {
        this.data = resultSet.rawData()
      } else {
        this.data = undefined
      }
      return (
        (resultSet && <Component resultSet={resultSet} />) ||
        (error && error.toString()) || <Skeleton active />
      )
    }
  }

  exportCSV() {
    const { employeeName } = this.props
    const { startDate, endDate } = this.props.reportFilterStore!
    const data = this.data
    if (data) {
      parseAndDownloadCSV(`${employeeName}-category-sales-${startDate}-${endDate}`, data)
    }
  }

  render() {
    const { companyId, employeeId, employeeName } = this.props
    const { startDateString, endDateString, selectedLocationIds } = this.props.reportFilterStore!

    const query = {
      measures: ['PaymentLines.productNet', 'PaymentLines.quantity'],
      timeDimensions: [
        {
          dimension: 'Payments.completedate',
          granularity: null,
          dateRange: [startDateString, endDateString],
        },
      ],
      dimensions: ['Categories.fullname'],
      filters: [
        {
          dimension: 'Locations.id',
          operator: 'equals',
          values: selectedLocationIds.map((id) => `${id}`),
        },
        {
          dimension: 'Payments.status',
          operator: 'equals',
          values: [`completed`],
        },
        {
          member: this.props.employeeToReport ? this.props.employeeToReport : 'Payments.employeeid',
          operator: 'equals',
          values: [employeeId],
        },
        {
          member: 'Categories.name',
          operator: 'set',
        },
      ],
      order: {
        [this.state.order.field]: this.state.order.direction,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    return (
      <Modal isOpen={true} toggle={() => this.props.onClose()}>
        <ModalHeader toggle={() => this.props.onClose()}>{employeeName}</ModalHeader>
        <ModalBody>
          <div>
            <b>Category sales</b>
          </div>
          <div className='text-muted'>Does not include taxes</div>

          <div className='mt-2 mb-2'>
            <button className='btn btn-light btn-sm' onClick={() => this.exportCSV()}>
              Export as csv
            </button>
          </div>

          <div className='table-responsive'>
            <QueryRenderer
              query={query}
              cubejsApi={getCubeAPI(companyId)}
              render={this.renderChart(this.tableRenderer)}
            />
          </div>
        </ModalBody>
      </Modal>
    )
  }
}
